import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isEmpty, map } from 'lodash';

import { Vehicle, VehicleConstants } from '@sm360/widget-commons-vehicle';

import * as actions from 'inventory-listing/actions/listingUsedActions.js';
import { DEFAULT_PAGE_SIZE } from 'inventory-listing/constants/listing.js';
import ListingUsed from 'inventory-listing/components/ListingUsed/ListingUsed.jsx';
import VehicleCriteriaModel from 'inventory-listing/models/VehicleCriteriaModel';

const QUERY = gql`
    query fetchUsedVehicles(
        $after: String
        $first: Int!
        $organizationUnitId: Int!
        $paymentCriteria: PaymentCriteria
        $vehicleCriteria: VehicleCriteria
        $vehicleOrders: [VehicleOrder]
    ) {
        searchVehicles(
            after: $after
            first: $first
            organizationUnitId: $organizationUnitId
            paymentCriteria: $paymentCriteria
            vehicleCriteria: $vehicleCriteria
            vehicleOrders: $vehicleOrders
        ) {
            pageInfo {
                endCursor
                hasNextPage
                hasPreviousPage
                startCursor
            }
            totalCount
            nodes {
                id
                odometer
                onHold
                stockNo
                tagline
                vin
                year
                characteristics {
                    transmission {
                        label
                        type
                        driveTrain {
                            label
                        }
                    }
                }
                financePaymentInfo {
                    cashdown
                    interestRatePercent
                    payment
                    paymentFrequency
                    paymentTaxes
                    termInMonths
                }
                flags {
                    label
                    type
                }
                make {
                    id
                    name
                }
                model {
                    id
                    name
                }
                trim {
                    id
                    name
                }
                multimedia {
                    mainPicture {
                        url
                    }
                }
                prices {
                    regular
                    sale
                }
                inventory {
                    organizationUnit {
                        name
                    }
                }
            }
        }
    }
`;

const ListingUsedWithApollo = graphql(QUERY, {
    options: ({ organizationUnitId, pageNumber, vehicleCriteria, vehicleOrders, defaultVehicleOrders }) => {
        const criteria = vehicleCriteria || new VehicleCriteriaModel();

        const variables = {
            first: DEFAULT_PAGE_SIZE * pageNumber,
            organizationUnitId,
            vehicleCriteria: criteria.toJS(),
            vehicleOrders: !vehicleOrders || isEmpty(vehicleOrders) ? defaultVehicleOrders : vehicleOrders,
        };

        return { variables };
    },
    props: ({ data: { loading, searchVehicles, fetchMore } }) => {
        let vehicles = [];
        let pageInfo = {};
        let totalCount = 0;

        if (!loading && searchVehicles) {
            pageInfo = searchVehicles.pageInfo; // eslint-disable-line prefer-destructuring
            totalCount = searchVehicles.totalCount; // eslint-disable-line prefer-destructuring

            vehicles = map(searchVehicles.nodes, (node) => {
                let vehicleConditionType = VehicleConstants.CONDITIONS.USED;
                if (!isEmpty(node.flags)) {
                    const isCertified =
                        node.flags.filter((flag) => {
                            return flag.type === 'CERTIFIED';
                        }).length > 0;

                    vehicleConditionType = isCertified
                        ? VehicleConstants.CONDITIONS.CERTIFIED
                        : VehicleConstants.CONDITIONS.USED;
                }

                return new Vehicle.VehicleModel({
                    discount: node.prices.regular ? node.prices.regular - node.prices.sale : 0,
                    driveTrain: node.characteristics.transmission.driveTrain.label,
                    flags: node.flags,
                    organizationUnitName: node.inventory.organizationUnit.name,
                    locked: node.onHold,
                    make: node.make.name,
                    model: node.model.name,
                    trim: node.trim.name,
                    vehicleConditionType,
                    multimedia: new Vehicle.MultiMediaModel(node.multimedia),
                    odometer: node.odometer,
                    sellingPrice: node.prices.sale,
                    stockNumber: node.stockNo,
                    tagLine: node.tagline,
                    transmission: node.characteristics.transmission.label,
                    vehicleId: node.id,
                    vin: node.vin,
                    warranty: node.warranty,
                    year: node.year,
                });
            });
        }

        return {
            vehicles,
            pageInfo,
            totalCount,
            isLoading: loading,
            loadMore(endCursor) {
                return fetchMore({
                    variables: {
                        after: endCursor,
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        const oldNodes = previousResult.searchVehicles.nodes;
                        const newNodes = fetchMoreResult.searchVehicles.nodes;

                        if (!fetchMoreResult.searchVehicles) {
                            return previousResult;
                        }

                        return {
                            ...previousResult,
                            searchVehicles: {
                                ...previousResult.searchVehicles,
                                pageInfo: {
                                    ...previousResult.searchVehicles.pageInfo,
                                    ...fetchMoreResult.searchVehicles.pageInfo,
                                },
                                nodes: [...oldNodes, ...newNodes],
                            },
                        };
                    },
                });
            },
            applyFilter(model) {
                return fetchMore({
                    variables: {
                        vehicleCriteria: model.vehicleCriteria,
                        vehicleOrders: model.vehicleOrders,
                    },
                    updateQuery: (prev, { fetchMoreResult }) => {
                        if (!fetchMoreResult.searchVehicles) {
                            return prev;
                        }
                        return fetchMoreResult;
                    },
                });
            },
        };
    },
})(ListingUsed);

function mapStateToProps({ listingUsed }) {
    const {
        vehicleCriteria,
        defaultVehicleOrders,
        vehicleOrders,
        carfaxKey,
        colanderList,
        colanders,
        vdpUrl,
    } = listingUsed;

    return {
        vehicleCriteria,
        defaultVehicleOrders,
        vehicleOrders,
        carfaxKey,
        colanders,
        colanderList,
        vdpUrl,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(actions, dispatch),
    };
}

export { ListingUsed, ListingUsedWithApollo };

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ListingUsedWithApollo);



// WEBPACK FOOTER //
// ./client/src/containers/ListingUsed/ListingUsed.js